import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { CalendarClock, Check, Sparkles, X } from 'lucide-react';
import { SugestaoData } from '../../../src/lib/sugerirData';

interface RemanejarLeadsModalProps {
    /** 'leads' = remanejar leads selecionados · 'rota' = alterar a data da rota inteira. */
    modo: 'leads' | 'rota';
    titulo: string;
    count: number;
    /** Data de origem (YYYY-MM-DD). */
    dataAtual: string;
    /** Sugestão inteligente de data (agenda + região), quando disponível. */
    sugestao?: SugestaoData | null;
    salvando: boolean;
    onConfirm: (novaData: string, motivo: string) => void;
    onClose: () => void;
}

const isoDate = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const addDays = (n: number) => { const d = new Date(); d.setDate(d.getDate() + n); return isoDate(d); };
const fmtLongo = (iso: string) => new Date(iso + 'T12:00:00').toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long' });

/** Remanejamento (Motoboy v3): data + motivo opcional + sugestão inteligente, com histórico auditável. */
const RemanejarLeadsModal: React.FC<RemanejarLeadsModalProps> = ({ modo, titulo, count, dataAtual, sugestao, salvando, onConfirm, onClose }) => {
    const hoje = isoDate(new Date());
    const [data, setData] = useState(sugestao?.data || addDays(1));
    const [motivo, setMotivo] = useState('');

    const atalhos: Array<{ label: string; value: string }> = [
        { label: 'Amanhã', value: addDays(1) },
        { label: 'Depois de amanhã', value: addDays(2) },
        { label: 'Próxima semana', value: addDays(7) },
    ];

    return createPortal(
        <div className="fixed inset-0 z-[2000] flex items-end sm:items-center justify-center sm:p-4 bg-black/50" onClick={onClose}>
            <div
                className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md flex flex-col overflow-hidden max-h-[92vh]"
                onClick={e => e.stopPropagation()}
            >
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
                    <div className="flex items-center gap-2.5 min-w-0">
                        <div className="p-2 rounded-xl shrink-0 bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400">
                            <CalendarClock size={18} />
                        </div>
                        <div className="min-w-0">
                            <h2 className="text-sm font-bold text-gray-900 dark:text-white">
                                {modo === 'rota' ? 'Alterar Data da Rota' : 'Remanejar Leads'}
                            </h2>
                            <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                                {titulo} · {count} {modo === 'rota' ? (count === 1 ? 'parada pendente' : 'paradas pendentes') : (count === 1 ? 'lead' : 'leads')}
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-full text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 shrink-0">
                        <X size={18} />
                    </button>
                </div>

                <div className="p-5 space-y-4 overflow-y-auto">
                    <p className="text-xs text-gray-500 dark:text-gray-400 capitalize-first">
                        De <b className="text-gray-700 dark:text-gray-200 capitalize">{fmtLongo(dataAtual)}</b> para:
                    </p>

                    {sugestao && (
                        <button
                            onClick={() => setData(sugestao.data)}
                            className={`w-full text-left rounded-xl border p-3 transition-all ${data === sugestao.data
                                ? 'border-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 ring-1 ring-emerald-300/60'
                                : 'border-emerald-200 dark:border-emerald-800 bg-emerald-50/40 dark:bg-emerald-900/10 hover:border-emerald-400'}`}
                        >
                            <p className="text-xs font-bold text-emerald-700 dark:text-emerald-300 flex items-center gap-1.5">
                                <Sparkles size={13} /> Sugestão inteligente
                            </p>
                            <p className="text-[11px] text-emerald-700/80 dark:text-emerald-400/80 mt-0.5">{sugestao.texto}</p>
                        </button>
                    )}

                    <div className="grid grid-cols-3 gap-1.5">
                        {atalhos.map(a => (
                            <button
                                key={a.label}
                                onClick={() => setData(a.value)}
                                className={`px-2 py-2 rounded-xl text-[11px] font-semibold border transition-all leading-tight ${data === a.value
                                    ? 'bg-primary/10 border-primary/40 text-primary'
                                    : 'bg-gray-50 dark:bg-gray-700/40 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-gray-300'}`}
                            >
                                {a.label}
                            </button>
                        ))}
                    </div>

                    <div>
                        <label className="text-xs font-bold text-gray-700 dark:text-gray-200 block mb-1.5">Ou escolha no calendário</label>
                        <input
                            type="date"
                            value={data}
                            min={hoje}
                            onChange={e => setData(e.target.value)}
                            className="w-full px-3 py-2.5 text-sm rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                        />
                    </div>

                    <div>
                        <label className="text-xs font-bold text-gray-700 dark:text-gray-200 block mb-1.5">
                            Motivo do remanejamento <span className="text-gray-400 font-normal">(opcional)</span>
                        </label>
                        <textarea
                            value={motivo}
                            onChange={e => setMotivo(e.target.value)}
                            rows={2}
                            placeholder="Ex: não deu tempo de concluir a rota, cliente pediu outro dia..."
                            className="w-full px-3 py-2.5 text-sm rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none"
                        />
                    </div>

                    <p className="text-[11px] text-gray-400 capitalize">📅 {fmtLongo(data)}</p>
                    <p className="text-[11px] text-gray-400">A alteração fica registrada no histórico e visível ao Líder Comercial.</p>

                    <button
                        onClick={() => data && onConfirm(data, motivo.trim())}
                        disabled={!data || salvando}
                        className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-bold rounded-xl bg-primary text-white hover:opacity-90 transition-opacity disabled:opacity-40"
                    >
                        <Check size={16} />
                        {salvando ? 'Salvando...' : 'Confirmar remanejamento'}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default RemanejarLeadsModal;
